import {computed, inject, Injectable, signal} from '@angular/core';
import {Subscription} from 'rxjs';
import {User} from '../../api/models/user';
import {UserRole} from '../../api/models/user-role';
import {UserApiService} from '../../api/services/user-api.service';
import {OAuthService} from 'angular-oauth2-oidc';

@Injectable({ providedIn: 'root' })
export class UserStore {
  private userApiService = inject(UserApiService);
  private oauthService = inject(OAuthService);

  // Signals
  private _user = signal<User | undefined>(undefined);
  private _scopes = signal<string[]>([]);

  // Public readonly signals
  currentUser = computed(() => this._user());
  currentUserServices = computed(() => this._user()?.services);
  isAdmin = computed(() => this.hasRole('ADMIN'));
  isDirector = computed(() => this.hasRole('DIRECTOR'));
  isQhse = computed(() => this.hasRole('QHSE'));
  isAccountant = computed(() => this.hasRole('ACCOUNTANT'));
  isBuyer = computed(() => this.hasRole('BUYER'));

  // Scope-based computed signals
  canReadAccountingCode = computed(() => this.hasScope('eit:accounting-code.read'));
  canReadPurchaseOrder = computed(() => this.hasScope('eit:purchase-order.read'));
  canCreatePurchaseOrder = computed(() => this.hasScope('eit:purchase-order.create'));
  canReadSupplier = computed(() => this.hasScope('eit:supplier.read'));
  canCreateDeliveryOrder = computed(() => this.hasScope('eit:delivery-order.create'));
  canUpdateDeliveryOrder = computed(() => this.hasScope('eit:delivery-order.update'));
  canUpdatePurchaseOrderAmountValidation = computed(() => this.hasScope('eit:purchase-order:amount-validation.update'));
  canUpdatePurchaseOrderGreenlight = computed(() => this.hasScope('eit:purchase-order:greenlight.update'));
  canCreateInvoice = computed(() => this.hasScope('eit:invoice.create'));
  canReadInvoiceDocument = computed(() => this.hasScope('eit:invoice-document.read'));
  canUpdateInvoiceDocument = computed(() => this.hasScope('eit:invoice-document.update'));

  private hasRole(role: UserRole): boolean {
    return this._user()?.roles?.includes(role) ?? false;
  }

  hasScope(scope: string): boolean {
    return this._scopes().includes(scope);
  }

  // Load user from API and update the signal
  loadUser(): Subscription {
    return this.userApiService.getConnectedUser().subscribe(user => {
      this._user.set(user);
    });
  }

  // Parse granted scopes from the OAuth token and store them
  loadScopes(): void {
    const granted = this.oauthService.getGrantedScopes();
    if (!granted) {
      this._scopes.set([]);
      return;
    }
    const scopes = typeof granted === 'string'
      ? (granted as string).split(' ').filter(s => s.length > 0)
      : granted as string[];
    this._scopes.set(scopes);
  }
}
